import { SoapTemplate } from "src/app/backend/models/soap-template";
import {
  HealthRecord,
  HealthRecordAttachment,
  HealthRecordDetailSOAP
} from "src/app/backend/models/health-record";
import {
  ReportDesignerItemCustom,
  ReportDesignItem,
  ReportDesignItemConfig
} from "src/app/modules/bootstrap/modules/report/report-design-item";
import { SoapTemplateDiagnosisComponent } from "src/app/modules/bootstrap/pms-ui/modules/organization/soap-template/soap-template-editor/soap-template-diagnosis/soap-template-diagnosis.component";
import {
  compact,
  forOwn,
  isArray,
  isObject,
  isString,
  uniqBy
} from "lodash-es";
import { SoapTemplateSummaryComponent } from "src/app/modules/bootstrap/pms-ui/modules/organization/soap-template/soap-template-editor/soap-template-summary/soap-template-summary.component";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { dateUtil } from "src/app/support/date-util";
import { MemberBackend } from "src/app/backend/api/member.backend";
import { TemplateService } from "src/app/services/template.service";
import { defer, forkJoin, iif, lastValueFrom, of } from "rxjs";
import { Member, MemberLight } from "src/app/backend/models/member/member";
import { getCustomerNominative } from "src/app/support/customer";
import { SoapTemplateAttachmentsComponent } from "src/app/modules/bootstrap/pms-ui/modules/organization/soap-template/soap-template-editor/soap-template-attachments/soap-template-attachments.component";
import { PetBackend } from "src/app/backend/api/pet.backend";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { catchError, map, switchMap } from "rxjs/operators";
import { buildSoapDesignModel } from "src/app/backend/models/soap/soap-design";
import {
  Customer,
  ExternalCustomer
} from "../backend/models/customer/customer";
import { Pet } from "src/app/backend/models/pet/pet";
import { tryParseJson } from "src/app/support/json";
import { PhysicianBackend } from "src/app/backend/api/physician.backend";
import { PhysicianLight } from "src/app/backend/models/physician";

export function soapTemplateWithHealthRecordoToTemplateItems(
  soapTemplate: SoapTemplate,
  healthRecord: HealthRecord<HealthRecordDetailSOAP>,
  healthRecordAttachments: HealthRecordAttachment[],
  memberBackend: MemberBackend,
  physicianBackend: PhysicianBackend,
  templateService: TemplateService,
  patientBackend: PetBackend,
  customerBackend: CustomerBackend,
  customer: Customer | undefined,
  patient: Pet | undefined,
  options?: {
    readOnly?: boolean;
    hideCustomerAndPatientData?: boolean;
    checkedInAt?: string;
  }
): ReportDesignItemConfig[] {
  const soapDetail: HealthRecordDetailSOAP | undefined = healthRecord.detail as
    | HealthRecordDetailSOAP
    | undefined;

  let subjective: ReportDesignItemConfig | undefined = tryParseJson(
    soapTemplate.detail.subjective!,
    undefined
  );
  if (subjective && isArray(subjective)) {
    subjective = subjective[0];
  }

  let objective: ReportDesignItemConfig | undefined = tryParseJson(
    soapTemplate.detail.objective!,
    undefined
  );
  if (objective && isArray(objective)) {
    objective = objective[0];
  }

  let assessment: ReportDesignItemConfig | undefined = tryParseJson(
    soapTemplate.detail.assessment!,
    <any>undefined
  );
  if (assessment && isArray(assessment)) {
    assessment = assessment[0];
  }

  let plan: ReportDesignItemConfig = tryParseJson(
    soapTemplate.detail.plan!,
    <any>undefined
  );
  if (plan && isArray(plan)) {
    plan = plan[0];
  }

  const signature: ReportDesignItemConfig[] = tryParseJson(
    soapTemplate.detail.signature,
    <any>[]
  );

  if (subjective && soapDetail?.subjective?.content) {
    hydrateValues(subjective, soapDetail?.subjective?.content || {});
  }

  if (objective && soapDetail?.objective?.content) {
    hydrateValues(objective, soapDetail?.objective?.content || {});
  }

  if (assessment && soapDetail?.assessment?.content) {
    hydrateValues(assessment, soapDetail?.assessment?.content || {});
  }

  if (plan && soapDetail?.plan?.content) {
    hydrateValues(plan, soapDetail?.plan?.content || {});
  }

  signature.forEach((x) =>
    hydrateValues(x, soapDetail?.signature?.content || {})
  );

  if (assessment?.config?.items) {
    const diagnosisItem = assessment.config.items.find(
      (x: ReportDesignItem) => x.type === "custom"
    );
    if (diagnosisItem) {
      diagnosisItem.component = SoapTemplateDiagnosisComponent;
      diagnosisItem.config.preview = false;
      diagnosisItem.config.tags =
        healthRecord.detail?.diagnosis ||
        healthRecord.detail?.assessment?.tags ||
        [];
    }
  }

  const items = [subjective, objective, assessment, plan, ...signature];

  const filtered = items.filter((item) => {
    if (item?.type === "custom") {
      return false;
    }

    if (item?.config) {
      if (item.config.hidden) {
        return false;
      }
    }

    return true;
  });

  return compact([
    getSummaryReportDesignerItemCustom(
      soapTemplate,
      healthRecord,
      memberBackend,
      physicianBackend,
      templateService,
      patientBackend,
      customerBackend,
      customer,
      patient,
      {
        readOnly: !!options?.readOnly,
        hideCustomerAndPatientData: !!options?.hideCustomerAndPatientData,
        checkedInAt: options?.checkedInAt
      }
    ),
    ...filtered,
    options?.readOnly
      ? null
      : getAttachmentsReportDesignItemCustom(
          healthRecord,
          healthRecordAttachments
        )
  ]);
}

function getSummaryReportDesignerItemCustom(
  soapTemplate: SoapTemplate,
  healthRecord: HealthRecord,
  memberBackend: MemberBackend,
  physicianBackend: PhysicianBackend,
  templateService: TemplateService,
  patientBackend: PetBackend,
  customerBackend: CustomerBackend,
  customer: Customer | undefined,
  patient: Pet | undefined,
  options?: {
    readOnly?: boolean;
    hideCustomerAndPatientData?: boolean;
    checkedInAt?: string;
  }
): ReportDesignerItemCustom {
  const soapDesignModel = buildSoapDesignModel(
    healthRecord,
    customer,
    patient,
    options?.checkedInAt
  );
  return <ReportDesignerItemCustom>{
    type: "custom",
    component: SoapTemplateSummaryComponent,
    config: {},
    icon: "",
    title: sntTranslate(marker("Sommario")),
    showHeaderTitle: true,
    sticky: true,
    baseConfig: {
      healthRecord: healthRecord,
      customer: customer,
      patient: patient,
      title: healthRecord.name || soapTemplate.name,
      date: dateUtil().toISOString(),
      readOnly: options?.readOnly,
      hideCustomerAndPatientData: options?.hideCustomerAndPatientData || false,
      diagnosis:
        healthRecord.detail?.diagnosis ||
        healthRecord.detail?.assessment?.tags ||
        [],
      organs: healthRecord.detail?.organs || [],
      anamnesis: healthRecord.detail?.anamnesis || [],
      physicalExam: healthRecord.detail?.physicalExam || [],
      laboratoryExams: healthRecord.detail?.laboratoryExams || [],
      imagingDiagnostics: healthRecord.detail?.imagingDiagnostics || [],
      internalNote: healthRecord.note || soapTemplate.note,
      services: [],
      staff: healthRecord.staff ?? [],
      tags: healthRecord.tags || [],
      soapDesignModel: soapDesignModel,
      weight: healthRecord.detail?.weight || "",
      html: (item: ReportDesignerItemCustom) =>
        getSoapTemplateSummaryHtml(
          memberBackend,
          physicianBackend,
          templateService,
          item,
          patientBackend,
          customerBackend,
          healthRecord
        )
    }
  };
}

function getAttachmentsReportDesignItemCustom(
  healthRecord: HealthRecord,
  healthRecrodAttachments: HealthRecordAttachment[]
): ReportDesignerItemCustom {
  return <ReportDesignerItemCustom>{
    type: "custom",
    component: SoapTemplateAttachmentsComponent,
    config: {},
    icon: "",
    title: sntTranslate(marker("Allegati")),
    showHeaderTitle: true,
    sticky: true,
    baseConfig: {
      healthRecordId: healthRecord.id,
      attachments: healthRecrodAttachments
    }
  };
}

export async function getSoapTemplateSummaryHtml(
  memberBackend: MemberBackend,
  physicianBackend: PhysicianBackend,
  templateService: TemplateService,
  item: ReportDesignerItemCustom,
  patientBackend: PetBackend,
  customerBackend: CustomerBackend,
  healthRecord?: HealthRecord
) {
  const soapSummaryTemplate = await fetch(
    "/assets/ejs-templates/soap-summary.ejs"
  ).then((response) => response.text());

  const staffSource: Array<string | { id: string }> = healthRecord
    ? (item.config?.staff ?? healthRecord.staff ?? [])
    : item.config?.staff || item.baseConfig?.staff || [];

  const staffIds: string[] = compact(
    staffSource.map((x: string | { id: string }) => {
      if (isString(x)) {
        return x;
      }

      return x.id;
    })
  );

  const { staff, patient, customer } = await lastValueFrom(
    forkJoin({
      patient: iif(
        () => !!healthRecord?.patient?.id,
        defer(() => patientBackend.loadLight(healthRecord?.patient.id!)),
        iif(
          () => !!item.baseConfig?.patient,
          defer(() =>
            iif(
              () => !!item.baseConfig?.patient?.id,
              defer(() =>
                patientBackend.loadLight(item.baseConfig?.patient?.id)
              ),
              of({
                name: item.baseConfig?.patient?.name || ""
              })
            )
          ),
          of(undefined)
        )
      ),
      customer: iif<ExternalCustomer, ExternalCustomer>(
        () => !!healthRecord?.patient?.id,
        defer(() =>
          patientBackend.loadLight(healthRecord?.patient.id!).pipe(
            switchMap((patient) => {
              return customerBackend.loadLight(patient.owner!.id).pipe(
                map((customer) => ({
                  firstName: customer.firstName || "",
                  lastName: customer.lastName || "",
                  businessName: customer.businessName || ""
                }))
              );
            })
          )
        ),
        iif<ExternalCustomer, ExternalCustomer>(
          () => !!item.baseConfig?.customer,
          defer(() =>
            iif(
              () => !!item.baseConfig?.customer?.id,
              defer(() =>
                customerBackend.loadLight(item.baseConfig?.customer?.id).pipe(
                  map((customer) => ({
                    firstName: customer.firstName || "",
                    lastName: customer.lastName || "",
                    businessName: customer.businessName || ""
                  }))
                )
              ),
              of<{ firstName: string; lastName: string; businessName: string }>(
                {
                  firstName: item.baseConfig?.customer?.firstName || "",
                  lastName: item.baseConfig?.customer?.lastName || "",
                  businessName: item.baseConfig?.customer?.businessName || ""
                }
              )
            )
          ),
          of({
            firstName: "",
            lastName: "",
            businessName: ""
          })
        )
      ),
      staff: iif(
        () => staffIds.length > 0,
        defer(() =>
          forkJoin(
            staffIds.map((id) =>
              memberBackend.loadLight(id).pipe(catchError(() => of(null)))
            )
          )
        ),
        of([])
      ).pipe(
        map((staffList: Array<MemberLight | null>) => {
          return uniqBy(compact(staffList), (x) => x.id);
        })
      )
    })
  );

  const data = buildSoapDesignModel(
    healthRecord,
    customer,
    patient,
    healthRecord?.ticket?.checkedInAt
  );

  data.staffLines = staff.map((x) => {
    let line = `${getCustomerNominative(x)}`;

    const professionalInfo =
      x?.professionalRegisterProvince && x?.professionalRegisterNumber
        ? `Albo ${x.professionalRegisterProvince}, n° ${x.professionalRegisterNumber}`
        : "";

    if (professionalInfo) {
      line = `${line} • ${professionalInfo}`;
    }

    return line;
  });

  if (patient && (<Pet>patient).referringPhysician) {
    data.referringPhysician = await lastValueFrom(
      physicianBackend.loadLight((<Pet>patient).referringPhysician!).pipe(
        map((member: PhysicianLight) => {
          return getCustomerNominative(member);
        })
      )
    );
  }

  if (item.baseConfig?.soapDesignModel) {
    data.reportLine = item.baseConfig.soapDesignModel.reportLine;
    data.patientLine = item.baseConfig.soapDesignModel.patientLine;
    data.checkedInAtLine =
      item.baseConfig.soapDesignModel.checkedInAtLine ?? data.checkedInAtLine;
  }

  if (item.baseConfig?.esecutionAt) {
    data.reportLine = `${data.reportLine} • Eseguito il ${item.baseConfig.esecutionAt}`;
  }

  if (item.baseConfig?.reportingAt) {
    data.reportLine = `${data.reportLine} • Refertato il ${item.baseConfig.reportingAt}`;
  }

  return templateService.render(soapSummaryTemplate, {
    refertoLabel: sntTranslate(marker("Referto")),
    clienteLabel: sntTranslate(marker("Cliente")),
    pazienteLabel: sntTranslate(marker("Paziente")),
    autoreLabel: sntTranslate(marker("Autore")),
    autoriLabel: sntTranslate(marker("Autori")),
    checkInLabel: sntTranslate(marker("Data primo check-in")),
    vetReferenteLabel: sntTranslate(marker("Vet. referente")),
    healthRecordNumber: healthRecord?.number || "",
    healthRecordCreatedAt: healthRecord?.createdAt
      ? dateUtil(healthRecord.createdAt).format("DD/MM/YYYY")
      : "",
    healthRecordCompletedAt: healthRecord?.completedAt
      ? dateUtil(healthRecord.completedAt).format("DD/MM/YYYY")
      : "",
    professionalRegisterInfo:
      staff[0]?.professionalRegisterProvince &&
      staff[0]?.professionalRegisterNumber
        ? `${staff[0].professionalRegisterProvince}, n° ${staff[0].professionalRegisterNumber}`
        : "",
    professionalRegisterSignedAt: "",
    staff,
    services: [],
    diagnosis: healthRecord?.detail?.assessment?.tags || [],
    data,
    getNominative(user: Member): string {
      return getCustomerNominative(user);
    },
    title: healthRecord?.name,
    createdAt: healthRecord
      ? dateUtil(healthRecord.performedAt).format("DD/MM/YYYY")
      : "",
    completedAt:
      healthRecord && healthRecord.completedAt
        ? dateUtil(healthRecord.completedAt).format("DD/MM/YYYY")
        : ""
  });
}

function hydrateValues(obj: any, valuesMap: Record<string, any>): void {
  forOwn(obj, (value, key) => {
    if (isObject(value)) {
      hydrateValues(value, valuesMap);
    }

    const valueMapped = valuesMap[obj.config?.name];
    if (valueMapped) {
      obj.config.value = valueMapped;
      obj.text = valueMapped;
      obj.value = valueMapped;
    }
  });
}

export function getHealthRecordFileName(
  omitFileExtension: boolean = false
): string {
  let text = `${sntTranslate(marker("Scheda paziente"))}`;

  return omitFileExtension ? text : `${text}.pdf`;
}
